import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set up axios default headers + auto-refresh on 401
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }

    // Intercept 401s and try refresh
    const interceptor = axios.interceptors.response.use(
      response => response,
      async (err) => {
        const original = err.config;
        if (err.response?.status === 401 && !original._retry && !original.url?.includes('/auth/refresh') && !original.url?.includes('/auth/login')) {
          original._retry = true;
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              const res = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
              const newToken = res.data.access_token;
              localStorage.setItem('token', newToken);
              setToken(newToken);
              original.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(original);
            } catch {
              // Refresh failed, force logout
              localStorage.removeItem('token');
              localStorage.removeItem('refresh_token');
              setToken(null);
              setUser(null);
            }
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (err) {
          console.error('Auth check failed:', err);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  // Calculate trial days left
  const getTrialDaysLeft = useCallback(() => {
    if (!user || user.subscription_status === 'active') return null;
    if (!user.trial_started_at) return 7; // Default to 7 days if no trial start
    
    const trialStart = new Date(user.trial_started_at);
    const trialEnd = new Date(trialStart);
    trialEnd.setDate(trialEnd.getDate() + (user.trial_days || 7));
    
    const now = new Date();
    const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    
    return Math.max(0, daysLeft);
  }, [user]);

  // Check if trial is expired
  const isTrialExpired = useCallback(() => {
    if (!user) return false;
    if (user.subscription_status === 'active') return false;
    if (user.role === 'admin') return false; // Admins don't need to pay
    
    const daysLeft = getTrialDaysLeft();
    return daysLeft !== null && daysLeft <= 0;
  }, [user, getTrialDaysLeft]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (err) {
        console.error('Failed to refresh user:', err);
      }
    }
  }, [token]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      const { access_token, refresh_token: rt, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      if (rt) localStorage.setItem('refresh_token', rt);
      setToken(access_token);
      setUser(userData);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (email, password, displayName, referralCode = null) => {
    setError(null);
    try {
      const payload = {
        email,
        password,
        display_name: displayName
      };
      
      // Add referral code if provided
      if (referralCode) {
        payload.referral_code = referralCode;
      }
      
      const response = await axios.post(`${API}/auth/register`, payload);
      const { access_token, refresh_token: rt, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      if (rt) localStorage.setItem('refresh_token', rt);
      setToken(access_token);
      setUser(userData);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    // Empty trash before logging out
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      try {
        const response = await axios.delete(`${API}/emails/trash/empty`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        console.log('Trash emptied on logout:', response.data);
      } catch (err) {
        console.error('Failed to empty trash on logout:', err);
      }
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (displayName) => {
    try {
      const response = await axios.put(`${API}/auth/me`, null, {
        params: { display_name: displayName }
      });
      setUser(response.data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Update failed' };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API}/auth/change-password`, null, {
        params: { current_password: currentPassword, new_password: newPassword }
      });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Password change failed' };
    }
  };

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isSupport: user?.role === 'support' || user?.role === 'admin',
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    getTrialDaysLeft,
    isTrialExpired,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
